import { useRef } from 'react'
import { useSubscription } from '@apollo/client/react'
import { AdminAPI, getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'

import { useAuth } from '@/features/auth'

export function useFeatureSubscription() {
  const { isSignedIn, hasOrg } = useAuth()
  const prevSyncingRef = useRef<GraphQL.SyncableObject[]>([])

  useSubscription(AdminAPI.integration.FEATURE_UPDATED, {
    skip: !isSignedIn || !hasOrg,
    onData({ client, data }) {
      const feature = getFragment(
        SharedAPI.integration.FEATURE_FRAGMENT,
        data.data?.integrationFeatureUpdated
      )

      if (!feature) return

      const prevSync = prevSyncingRef.current ?? []
      const currSync = feature.syncing ?? []

      client.cache.updateQuery(
        {
          query: AdminAPI.integration.GET_FEATURE,
          variables: {
            id: feature.id
          }
        },
        () => {
          return {
            getIntegrationFeature: feature
          }
        }
      )

      client.cache.updateQuery(
        { query: AdminAPI.integration.GET_ALL_FEATURES },
        data => {
          const features = getFragment(
            SharedAPI.integration.FEATURE_FRAGMENT,
            data?.getAllIntegrationFeatures
          )

          const syncedData = prevSync.filter(
            syncObject => !currSync.includes(syncObject)
          )

          syncedData?.forEach(syncObject => {
            switch (syncObject) {
              case GraphQL.SyncableObject.Rooms:
                client.cache.evict({ fieldName: 'Space' })
                client.cache.gc()
                client.refetchQueries({ include: [AdminAPI.space.QUERY] })

                return

              case GraphQL.SyncableObject.Stays:
                client.cache.evict({ fieldName: 'Stay' })
                client.cache.evict({ fieldName: 'Guest' })
                client.cache.gc()

                client.refetchQueries({
                  include: [AdminAPI.stay.QUERY, AdminAPI.guest.QUERY]
                })

                return

              case GraphQL.SyncableObject.Bookings:
                client.cache.evict({ fieldName: 'Order' })
                client.cache.gc()

                client.refetchQueries({
                  include: [
                    AdminAPI.order.GET_BY_EVENT,
                    AdminAPI.order.GET_BY_STAY,
                    AdminAPI.order.LIST_REQUESTS
                  ]
                })

                return
            }
          })

          prevSyncingRef.current = currSync

          const { items } = features?.reduce(
            (acc, f, idx, array) => {
              const isLast = array.length - 1 === idx

              if (f.id === feature.id) {
                acc.items.push(feature)
                acc.didUpdate = true
              } else {
                acc.items.push(f)
              }

              if (isLast && !acc.didUpdate) {
                acc.items.push(feature)
              }

              return acc
            },
            {
              items: [] as GraphQL.IntegrationFeatureFragmentFragment[],
              didUpdate: false
            }
          ) ?? { items: [feature] }

          return {
            getAllIntegrationFeatures: items
          }
        }
      )
    }
  })
}
